import { useContext, useEffect, useReducer, useRef, useState } from 'react'

import { Button, Paper, Typography } from '@material-ui/core'

import { BotIcon } from 'lucide-react'
import { toast } from 'react-toastify'
import ConfirmationModal from '../../components/ConfirmationModal'
import MainContainer from '../../components/MainContainer'
import MainHeader from '../../components/MainHeader'
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper'
import ProtectedRoute from '../../components/ProtectedRoute'
import QueueModal from '../../components/QueueModal'
import QuickTable from '../../components/QuickTable'
import { SocketContext } from '../../context/Socket/SocketContext'
import toastError from '../../errors/toastError'
import api from '../../services/api'
import { i18n } from '../../translate/i18n'
import { useStyles } from './styles'

const reducer = (state, action) => {
  if (action.type === 'LOAD_QUEUES') {
    const queues = action.payload
    const newQueues = []

    queues.forEach(queue => {
      const queueIndex = state.findIndex(q => q.id === queue.id)
      if (queueIndex !== -1) {
        state[queueIndex] = queue
      } else {
        newQueues.push(queue)
      }
    })

    return [...state, ...newQueues]
  }

  if (action.type === 'UPDATE_QUEUES') {
    const queue = action.payload
    const queueIndex = state.findIndex(u => u.id === queue.id)

    if (queueIndex !== -1) {
      const updatedState = [...state]
      updatedState[queueIndex] = queue
      return updatedState
    } else {
      return [queue, ...state]
    }
  }

  if (action.type === 'DELETE_QUEUE') {
    const queueId = action.payload
    const queueIndex = state.findIndex(q => q.id === queueId)
    if (queueIndex !== -1) {
      state.splice(queueIndex, 1)
    }
    return [...state]
  }

  if (action.type === 'RESET') {
    return []
  }
}

const Queues = () => {
  const classes = useStyles()

  // const { user } = useContext(AuthContext)

  const [queues, dispatch] = useReducer(reducer, [])
  const [loading, setLoading] = useState(false)

  const [queueModalOpen, setQueueModalOpen] = useState(false)
  const [selectedQueue, setSelectedQueue] = useState(null)
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)

  const [isMainContentHidden, setIsMainContentHidden] = useState(false)
  const buttonRef = useRef(null)

  const socketManager = useContext(SocketContext)

  const columns = [
    {
      field: 'name',
      headerName: i18n.t('queues.table.name'),
      align: 'center'
    },
    {
      field: 'description',
      headerName: 'Descrição',
      align: 'center'
    },
    {
      field: 'greetingMessage',
      headerName: i18n.t('queues.table.greeting'),
      align: 'center'
    }
    // {
    //   field: 'isBot',
    //   headerName: 'Bot/Departamento',
    //   align: 'center'
    // }
  ]

  useEffect(() => {
    if (queueModalOpen && buttonRef.current) {
      buttonRef.current.blur()
    }
  }, [queueModalOpen])

  useEffect(() => {
    (async () => {
      setLoading(true)
      try {
        const { data } = await api.get('/queue')
        dispatch({ type: 'LOAD_QUEUES', payload: data })

        setLoading(false)
      } catch (err) {
        toastError(err)
        setLoading(false)
      }
    })()
  }, [])

  useEffect(() => {
    const companyId = localStorage.getItem('companyId')
    const socket = socketManager.GetSocket(companyId)

    const onQueue = data => {
      if (data.action === 'update' || data.action === 'create') {
        dispatch({ type: 'UPDATE_QUEUES', payload: data.queue })
      }

      if (data.action === 'delete') {
        dispatch({ type: 'DELETE_QUEUE', payload: data.queueId })
      }
    }

    socket.on(`company-${companyId}-queue`, onQueue)

    return () => {
      socket.disconnect()
    }
  }, [socketManager])

  const handleOpenQueueModal = () => {
    setQueueModalOpen(true)
    setSelectedQueue(null)
    setIsMainContentHidden(true)
  }

  const handleCloseQueueModal = () => {
    setQueueModalOpen(false)
    setSelectedQueue(null)
    setIsMainContentHidden(false)
  }

  const handleEditQueue = queue => {
    setSelectedQueue(queue)
    setQueueModalOpen(true)
  }

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false)
    setSelectedQueue(null)
  }

  const handleDeleteQueue = async queueId => {
    try {
      await api.delete(`/queue/${queueId}`)
      toast.success(i18n.t('Queue deleted successfully!'))
    } catch (err) {
      toastError(err)
    }
    setSelectedQueue(null)
  }

  const customCellRenderer = {
    greetingMessage: queue => (
      <div className={classes.customTableCell}>
        <Typography
          style={{ width: 300, align: 'center' }}
          noWrap
          variant="body2"
        >
          {queue.greetingMessage}
        </Typography>
      </div>
    )
  }

  return (
    <ProtectedRoute
      requiredRoles={['super', 'admin', 'departamentos']}
      fallbackPath="/tickets"
    >
      <MainContainer aria-hidden={isMainContentHidden}>
        <ConfirmationModal
          title={
            selectedQueue &&
            `${i18n.t('queues.confirmationModal.deleteTitle')} ${selectedQueue.name
            }?`
          }
          open={confirmModalOpen}
          onClose={handleCloseConfirmationModal}
          onConfirm={() => handleDeleteQueue(selectedQueue.id)}
        >
          {i18n.t('queues.confirmationModal.deleteMessage')}
        </ConfirmationModal>
        <QueueModal
          open={queueModalOpen}
          onClose={handleCloseQueueModal}
          queueId={selectedQueue?.id}
        />
        <MainHeader
          title={i18n.t('queues.title')}
          description="Crie e configure bots que automatizam o atendimento, integrando departamentos para uma comunicação mais eficiente."
          showTutorial
        // onTutorialClick={handleOpenTutorial}
        >
          <MainHeaderButtonsWrapper>
            <Button
              variant="contained"
              onClick={handleOpenQueueModal}
              className={classes.buttonNewBot}
              startIcon={<BotIcon />}
              ref={buttonRef}
            >
              Novo Bot
            </Button>
          </MainHeaderButtonsWrapper>
        </MainHeader>
        <Paper className={classes.mainPaper}>
          <QuickTable
            data={queues?.filter(queue => queue.isBot) || []}
            columns={columns}
            loading={loading}
            actions={['edit', 'delete']}
            onEdit={handleEditQueue}
            onDelete={queue => {
              setSelectedQueue(queue)
              setConfirmModalOpen(true)
            }}
            customCellRenderer={customCellRenderer}
            emptyMessage="Nenhum departamento encontrado"
          />
        </Paper>
      </MainContainer>
    </ProtectedRoute>
  )
}

export default Queues
